import { useSyncExternalStore } from 'react';
import { useAuthStore } from '@/store/auth-store';
import { useMutation,useQueryClient } from '@tanstack/react-query';
import { client as sdkClient } from '@blazeteams/sdk/client';
import { isValidHex } from '@/lib/utils/contrast';
import { useThemeStore,hydrateTheme,confirmTheme,adoptStoredTheme,themeIntentRevision,themeHasUnsavedChanges,type ThemePrefs,type ThemeMode,type TimeFormat } from '@/store/theme-store';
import { LocalizedError } from '@/lib/i18n/errors';
import { personalOwner,requirePersonalOwner,personalHeaders,type PersonalOwner } from './session';
export interface UiPrefsBlob{theme_mode?:ThemeMode;accent?:string|null;bubble_out?:string|null;time_format?:TimeFormat}
const object=(value:unknown):value is Record<string,unknown>=>!!value&&typeof value==='object'&&!Array.isArray(value);
function color(value:unknown){return typeof value==='string'&&isValidHex(value)?value:null;}
export function blobToPrefs(blob:UiPrefsBlob|null|undefined):ThemePrefs{
 const b=blob??{};return {mode:b.theme_mode==='dark'||b.theme_mode==='system'?b.theme_mode:'light',accent:color(b.accent),bubbleOut:color(b.bubble_out),timeFormat:b.time_format==='24h'?'24h':'12h'};
}
export function prefsToBlob(p:ThemePrefs):UiPrefsBlob{return {theme_mode:p.mode,accent:p.accent,bubble_out:p.bubbleOut,time_format:p.timeFormat};}
export function appearanceKey(owner=personalOwner()){return ['ui-preferences','owner',owner.scope,owner.revision] as const;}
export function preferenceFailure(status:number,write=false){
 return new LocalizedError('appearance',status===401?'error.unauthorized':status===403?'error.forbidden':status===400?'error.badInput':write?'error.unconfirmed':'error.loadFailed',{},status);
}
export function readUIPreferences(raw:unknown,owner:PersonalOwner){
 if(!object(raw)||raw.success!==true||!object(raw.meta)||raw.meta.user_id!==owner.user||raw.meta.tenant_id!==owner.tenant||!object(raw.data))throw preferenceFailure(0);
 return {blob:structuredClone(raw.data),prefs:blobToPrefs(raw.data as UiPrefsBlob)};
}
async function fetchPreferences(owner:PersonalOwner,signal?:AbortSignal,hydrate=false){
 requirePersonalOwner(owner,signal);const revision=themeIntentRevision(),dirty=themeHasUnsavedChanges();let result;
 try{result=await sdkClient.get<unknown>({url:'/me/ui-preferences',headers:personalHeaders(owner),signal,cache:'no-store'});}
 catch{requirePersonalOwner(owner,signal);throw preferenceFailure(0);}
 requirePersonalOwner(owner,signal);
 if(result.error||result.response?.status!==200)throw preferenceFailure(result.response?.status??0);
 const receipt=readUIPreferences(result.data,owner);
 if(hydrate&&!dirty&&revision===themeIntentRevision())hydrateTheme(receipt.prefs);
 return receipt;
}
export const appearanceQueries={
 preferences:(owner=personalOwner())=>({
  queryKey:appearanceKey(owner),enabled:owner.scope!=='anonymous'&&!!owner.user&&!!owner.tenant,
  queryFn:({signal}:{signal:AbortSignal})=>fetchPreferences(owner,signal,true),
  retry:false as const,staleTime:30_000,refetchOnWindowFocus:'always' as const,refetchOnReconnect:'always' as const,
 }),
};
export type PreferenceIntent={owner:PersonalOwner;body:UiPrefsBlob;intent:number};
export function capturePreferenceIntent(blob?:UiPrefsBlob):PreferenceIntent{
 const state=useThemeStore.getState();
 return {owner:personalOwner(),body:JSON.parse(JSON.stringify({...prefsToBlob(state),...blob})),intent:themeIntentRevision()};
}
// One owned status coordinates the form and quick toggle. Unknown writes stop
// queued intents until an explicit GET review; they never retry automatically.
const listeners=new Set<()=>void>();
const idle=()=>({identity:JSON.stringify(appearanceKey()),pending:0,error:null as unknown,saved:false,quarantined:false});
let writes=idle();
const notify=()=>listeners.forEach(listener=>listener());
function publishWrite(owner:PersonalOwner,patch:Partial<typeof writes>){
 requirePersonalOwner(owner);const identity=JSON.stringify(appearanceKey(owner));
 writes={...(writes.identity===identity?writes:idle()),...patch,identity};notify();
}
useAuthStore.subscribe(()=>{const identity=JSON.stringify(appearanceKey());if(writes.identity!==identity){writes=idle();notify();}});
export function useAppearanceWriteState(){return useSyncExternalStore(listener=>{listeners.add(listener);return()=>listeners.delete(listener);},()=>writes);}
export function useUpdateUiPreferences(){
 const qc=useQueryClient(),identity=appearanceKey();
 const mutation=useMutation({
  scope:{id:JSON.stringify(identity)},retry:false,
  mutationFn:async(input:PreferenceIntent)=>{
   const {owner,body,intent}=input;requirePersonalOwner(owner);
   if(writes.quarantined)throw preferenceFailure(0,true);
   if(intent!==themeIntentRevision())throw new DOMException('Superseded','AbortError');
   publishWrite(owner,{pending:writes.pending+1,error:null,saved:false});
   try{
    // Native PUT replaces JSON. A fresh preimage preserves opaque fields;
    // this is not a cross-device CAS or merge transaction.
    const before=await fetchPreferences(owner);
    requirePersonalOwner(owner);
    if(intent!==themeIntentRevision())throw new DOMException('Superseded','AbortError');
    const snapshot={...before.blob,...body};let result;
    try{result=await sdkClient.put<unknown>({url:'/me/ui-preferences',headers:personalHeaders(owner,true),body:snapshot});}
    catch{requirePersonalOwner(owner);throw preferenceFailure(0,true);}
    requirePersonalOwner(owner);
    if(result.error||result.response?.status!==200)throw preferenceFailure(result.response?.status??0,true);
    let receipt;
    try{receipt=readUIPreferences(result.data,owner);if(JSON.stringify(prefsToBlob(receipt.prefs))!==JSON.stringify(body))throw new Error('Invalid preference receipt');}
    catch{throw preferenceFailure(0,true);}
    confirmTheme(receipt.prefs,intent);
    qc.setQueryData(appearanceKey(owner),receipt);
    publishWrite(owner,{saved:true});return receipt;
   }catch(error){
    requirePersonalOwner(owner);
    if(error instanceof DOMException&&error.name==='AbortError')throw error;
    publishWrite(owner,{error,quarantined:error instanceof LocalizedError&&error.key==='error.unconfirmed'});throw error;
   }finally{
    try{publishWrite(owner,{pending:Math.max(0,writes.pending-1)});}catch{/* retired owner */}
   }
  },
 });
 return {...mutation,
  mutateIntent:mutation.mutate,
  mutate:(blob?:UiPrefsBlob,options?:Parameters<typeof mutation.mutate>[1])=>mutation.mutate(capturePreferenceIntent(blob),options),
  mutateAsync:(blob?:UiPrefsBlob,options?:Parameters<typeof mutation.mutateAsync>[1])=>mutation.mutateAsync(capturePreferenceIntent(blob),options),
 };
}
export async function reviewStoredPreferences(owner:PersonalOwner){
 const receipt=await fetchPreferences(owner);requirePersonalOwner(owner);adoptStoredTheme(receipt.prefs);publishWrite(owner,{error:null,quarantined:false,saved:false});return receipt;
}
